import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VideoCameraIcon } from "@heroicons/react/24/outline";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  VideoCameraIcon as VideoCameraIconSolid,
} from "@heroicons/react/24/solid";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import dayjs from "dayjs";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function TimeLapseDialog({
  videos,
  cam,
  image,
  lastFullTimeLapseGeneration,
  mindate,
}: {
  cam: string;
  image?: string;
  lastFullTimeLapseGeneration?: string;
  mindate?: string;
  videos?: {
    [key: string]: string;
  };
}) {
  const [videoGallaryOpen, setVideoGallaryOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [tab, setTab] = useState<"full" | "weekly">(
    lastFullTimeLapseGeneration ? "full" : "weekly"
  );

  useEffect(() => {
    if (lastFullTimeLapseGeneration) {
      setTab("full");
    }
  }, [lastFullTimeLapseGeneration]);

  const [activeVideo, setActiveVideo] = useState<{
    image: string;
    sunday: string;
    key: string;
    start: string;
    end: string;
    url: string;
  }>();
  if (!videos) return null;

  const items = Object.keys(videos)
    .map((key) => {
      const dateKey = dayjs(key, "YYYY-MM-DD")
        .add(1, "days")
        .startOf("week")
        .add(1, "days")
        .format("YYYY/MM/DD");
      return {
        image: `https://clark-sullivan-cams.s3.us-west-1.amazonaws.com/projects/${cam.toUpperCase()}/images/${dateKey}/1100/output.jpg`,
        sunday: dayjs(key, "YYYY-MM-DD").format("YYYY/MM/DD"),
        key: dateKey,
        start: dayjs(key, "YYYY-MM-DD").format("MM/DD/YYYY"),
        end: dayjs(key, "YYYY-MM-DD")
          .add(1, "days")
          .endOf("week")
          .format("MM/DD/YYYY"),
        url: videos[key],
      };
    })
    .reverse();

  const fullTimelapseUrl = lastFullTimeLapseGeneration
    ? `https://clark-sullivan-cams.s3.us-west-1.amazonaws.com/projects/${cam.toUpperCase()}/full-timelapse.mp4`
    : null;
  const fullTimelapseActiveVideo = {
    image: fullTimelapseUrl,
    sunday: items[0].sunday,
    key: "full",
    start: dayjs(mindate, "YYYY-MM-DD").format("MM/DD/YYYY"),
    end: lastFullTimeLapseGeneration
      ? dayjs(lastFullTimeLapseGeneration, "YYYY-MM-DD")
          .add(1, "days")
          .endOf("week")
          .format("MM/DD/YYYY")
      : items[0].end,
    url: fullTimelapseUrl,
  };

  return (
    <>
      <Tooltip>
        <Dialog open={videoGallaryOpen} onOpenChange={setVideoGallaryOpen}>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (fullTimelapseUrl && tab !== "weekly") {
                  setActiveVideo(fullTimelapseActiveVideo as any);
                  setVideoOpen(true);
                } else {
                  setVideoGallaryOpen(true);
                }
              }}
              className="px-2 group"
              size="icon"
              variant={"link"}
            >
              <VideoCameraIcon className="h-5 w-5 group-hover:hidden" />
              <VideoCameraIconSolid className="h-5 w-5 group-hover:inline-flex hidden" />
            </Button>
          </TooltipTrigger>

          <DialogContent
            className="w-full flex flex-col max-w-none overflow-scroll h-[calc(100vh-4rem)] translate-y-0 bottom-0 top-[4rem]"
            dialogOverlayClassName="bg-background/20"
          >
            <DialogHeader>
              <DialogTitle>Timelapse Videos</DialogTitle>
            </DialogHeader>

            {lastFullTimeLapseGeneration ? (
              <Tabs
                onValueChange={(value) => {
                  if (value === "full") {
                    setVideoGallaryOpen(false);
                    setActiveVideo(fullTimelapseActiveVideo as any);
                    setVideoOpen(true);
                  }
                  setTab(value as any);
                }}
                value={tab}
                defaultValue="full"
                className="w-[400px]"
              >
                <TabsList>
                  <TabsTrigger value="full">Full Project</TabsTrigger>
                  <TabsTrigger value="weekly">Weekly</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-center gap-4">
              {items.map((item, index) => {
                return (
                  <button
                    key={item.key}
                    onClick={() => {
                      setActiveVideo(item);
                      setVideoOpen(true);
                    }}
                    className="grid-item aspect-h-9 aspect-w-16 relative rounded-lg overflow-hidden bg-gray-200"
                  >
                    <div>
                      <div className="absolute top-2 right-2 bg-black bg-opacity-40 text-white px-2.5 py-1 text-xs font-bold text-center rounded-full z-10 font-mono">
                        {item.start} - {item.end}
                      </div>

                      <Image
                        fill
                        alt={item.key}
                        unoptimized
                        src={item.image}
                        onError={(e) => {
                          // swap the image with the default image found at image &  Check that the src is already not the default image
                          if (image && e.currentTarget.src !== image) {
                            e.currentTarget.src = image;
                          }
                        }}
                        priority={index === 0}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        <TooltipContent side="bottom">
          <p>View time lapse</p>
        </TooltipContent>
      </Tooltip>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent
          className="max-w-[1080px] w-full"
          dialogOverlayClassName="bg-background/20"
        >
          {activeVideo ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  {activeVideo.start} - {activeVideo.end}
                  {tab === "weekly" || !lastFullTimeLapseGeneration ? (
                    <span className="flex items-center ml-4">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          const index = items.findIndex(
                            (item: any) => item.key === activeVideo.key
                          );
                          setActiveVideo(items[index - 1]);
                        }}
                        size="icon"
                        disabled={items[0].key === activeVideo.key}
                      >
                        <ArrowLeftIcon className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const index = items.findIndex(
                            (item: any) => item.key === activeVideo.key
                          );
                          setActiveVideo(items[index + 1]);
                        }}
                        disabled={
                          items[items.length - 1].key === activeVideo.key
                        }
                      >
                        <ArrowRightIcon className="h-5 w-5" />
                      </Button>
                    </span>
                  ) : null}
                </DialogTitle>
              </DialogHeader>

              {tab === "full" && lastFullTimeLapseGeneration ? (
                <Tabs
                  onValueChange={(value) => {
                    setVideoOpen(false);
                    setVideoGallaryOpen(true);
                    setTab(value as any);
                  }}
                  defaultValue="full"
                  className="w-[400px]"
                >
                  <TabsList>
                    <TabsTrigger value="full">Full Project</TabsTrigger>
                    <TabsTrigger value="weekly">Weekly</TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : null}

              <div className="aspect-h-9 aspect-w-16 relative rounded-lg overflow-hidden">
                <div>
                  <video
                    className="absolute inset-0 h-full"
                    src={activeVideo.url}
                    // poster={activeVideo.image}
                    controls
                    autoPlay
                    muted
                  />
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
